/*!
// <copyright file="ListDataProvider.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/// <reference path="../html-binding-controls.js" />
/// <reference path="../html-binding-handlers.ts" />
/// <reference path="Grid.js" />

(function () {
    'use strict';

    var CreateRowDirection = {
        RowCreatedAbove: -1,
        RowCreatedBelow: 1,
    };
    
    /**
    * @memberof $dyn.controls.List.DataProvider
    * @typedef DataProviderItem
    * @private
    *
    * @prop {Number} id
    * @prop {Number} version
    * @prop {Boolean} selected
    * @prop {String} type
    * @prop {Boolean} marked
    */

    /**
     * @class DataProvider
     * @classdesc The DataProvider component of the List control
     * @summary Responsible for providing an API to access and modify data of the List
     * @internal
     * @memberof $dyn.controls.List
     * @param {Object} options - Input data provider related external properties.
     */
    $dyn.controls.List.DataProvider = function (options) {
        var that = this;
        this._externalPropertyBag = options.PropertyBag;
        this.Collection = $dyn.observable();
        $dyn.observe(this.DataSource, function (dataSource) {
            that.Collection(dataSource);
        });

        if (options.GetDataId) {
            this._getDataId = options.GetDataId;
        }
        if (options.GetDataVersion) {
            this._getDataVersion = options.GetDataVersion;
        }
        this._enumerator = new $dyn.controls.List.DataProvider.Enumerator(this);

        this._internalRowIdToDataRowId = {};
        this._dataRowIdToInternalRowId = {};
        this.DataSelectedRowId = $dyn.cachedComputed(this._getDataSelectedRowId, null, this);
        this.InternalSelectedRowId = $dyn.observable(null);

        this.pendingRowToPivot = {};
        this.pivotToPendingRows = {};
        this._isCreateRowFromKeyDown = false;
        this.onRowBecameCommitted = new $dyn.eventHost();
        this._disposables = [];

        var activePageSelectedIndex = options.SelectedIndex;
    };

    $dyn.controls.List.DataProvider.prototype = {
        _internalRowIdToDataRowId: null,
        _dataRowIdToInternalRowId: null,
        _internalRowIdAllocator: 1,
        _rowInsertDirection: +1, // +1 - after/below, -1 - before/above
        _disposables: null,
        _enumerator: null,
        committedRowType: 'committed',
        _isCreateRowFromKeyDown: false,
        _externalPropertyBag: null,

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary The back end cache related index of the first record in the page
        * @type {Number}
        */
        get FetchedDataStartPosition() {
            return this._getExternalProperty('FetchedDataStartPosition');
        },
        set FetchedDataStartPosition(value) {
            this._setExternalProperty('FetchedDataStartPosition', value);
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary The size of the data cache
        * @type {Number}
        */
        get CacheSize() {
            return this._getExternalProperty('CacheSize');
        },
        set CacheSize(value) {
            this._setExternalProperty('CacheSize', value);
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Indicates whether the data can be fetched 'above'. 'Above' means indexes < 0
        * @type {Boolean}
        */
        get DataAbove() {
            return this._getExternalProperty('DataAbove');
        },
        set DataAbove(value) {
            this._setExternalProperty('DataAbove', value);
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Indicates whether the data can be fetched 'below'. 'Below' means indexes >= CacheSize
        * @type {Boolean}
        */
        get DataBelow() {
            return this._getExternalProperty('DataBelow');
        },
        set DataBelow(value) {
            this._setExternalProperty('DataBelow', value);
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Represents the data of the current active page
        * @type {Object}
        */
        get DataSource() {
            return this._getExternalProperty('DataSource');
        },
        set DataSource(value) {
            this._setExternalProperty('DataSource', value);
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Represents the active page related index of the selected row
        * @type {Number}
        */
        get SelectedIndex() {
            return this._getExternalProperty('SelectedIndex');
        },
        set SelectedIndex(value) {
            this._setExternalProperty('SelectedIndex', value);
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary An array of the marking row marking states
        * @type {Boolean[]}
        */
        get MarkedIndexes() {
            return this._getExternalProperty('MarkedIndexes');
        },
        set MarkedIndexes(value) {
            this._setExternalProperty('MarkedIndexes', value);
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary An event that is triggered when the data of the active page is changed on the back end
        * @type {Boolean}
        */
        get DataChanged() {
            return this._getExternalProperty('_dataChangedVersion');
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Same as DataSource
        * @type {Object}
        */
        Collection: null,

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Internal selected row id
        * @type {Observable|Number}
        */
        InternalSelectedRowId: null,

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Data selected row id
        * @type {Observable|Number}
        */
        DataSelectedRowId: null,

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary The method that returns row id by page index
        * @param {Number} pageIndex - Page index
        * @returns {Number} Row id
        */
        rowIdByPageIndexGetter: null,
        _getExternalProperty: function (propertyName) {
            if (this._externalPropertyBag) {
                return this._externalPropertyBag[propertyName];
            }
        },
        _setExternalProperty: function (propertyName, value) {
            if (this._externalPropertyBag) {
                this._externalPropertyBag[propertyName] = value;
            }
        },
        _nextDataId: 0,

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary The method that returns data id by given row data item
        * @param {Object} item - Row data item
        * @returns {Number} Row data id
        */
        _getDataId: function (item) {
            if (item && Object.prototype.toString.call(item) === "[object Object]") {
                if (!item.__listDataId) {
                    var dataId = ++$dyn.controls.List.DataProvider.prototype._nextDataId;
                    Object.defineProperty(item, '__listDataId', {
                        value: dataId,
                        enumerable: false,
                    });
                }

                return item.__listDataId;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary The method that returns data version by given row data item
        * @param {Object} item - Row data item
        * @returns {Number} Row data version
        */
        _getDataVersion: function (item) {
            return 1;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Allocates internal row id
        * @returns {Number} Internal row id
        */
        _allocateInternalRowId: function () {
            return this._internalRowIdAllocator++;
        },

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary The mapping from pending row (new row that doesn't exist on back end yet) to its pivot (the data row id of the exiting row)
        * @type {Dictionary}
        */
        pendingRowToPivot: {},

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary The mapping from pending row pivot (the data row id of the exiting row) to pending row (new row that doesn't exist on back end yet)
        * @type {Dictionary}
        */
        pivotToPendingRows: {},

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Indicates whether the given internal row id represents the pending row
        * @param {Number} _internalRowId - Internal row id
        * @returns {Boolean} True if row is pending
        */
        isRowPending: function (_internalRowId) {
            return this.pendingRowToPivot[_internalRowId];
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Indicates whether the List's active page has marked rows
        * @param {Boolean} excludeActiveRow - Indicates whether we have to exclude active row when figuring out whether List has marked rows
        * @returns {Boolean} True if the List has marked rows
        */
        hasMarkedRows: function (excludeActiveRow) {
            /// <summary>
            /// Determines if this grid control has marked rows.
            /// </summary>
            /// <returns>A Boolean value indicating if this grid control has marked rows.</returns>

            var foundMarkedRow = false;
            var rowCount = this.DataSource ? $dyn.length(this.DataSource) : 0;
            var markedIndexes = this.MarkedIndexes();

            for (var i = 0; i < rowCount && !foundMarkedRow; i++) {
                if (!excludeActiveRow || (i !== $dyn.peek(this.SelectedIndex))) {
                    foundMarkedRow = markedIndexes[i] === true;
                }
            }

            return foundMarkedRow;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary The getter of the DataSelectedRowId computed
        * @returns {Number} Data selected row id
        */
        _getDataSelectedRowId: function () {
            var dataSelectedRowId;
            var collection = $dyn.value(this.Collection);
            var selectedIndex = $dyn.value(this.SelectedIndex);
            // This is needed to react to model change that updated master tracking id without collection update (like refreshing the page)
            $dyn.value(this.DataChanged);
            if (selectedIndex >= 0 && selectedIndex < collection.length) {
                dataSelectedRowId = this.getOrCreateInternalRowId(this._getDataId($dyn.value(this.Collection)[$dyn.value(selectedIndex)]));
            } else {
                if (this.rowIdByPageIndexGetter) {
                    dataSelectedRowId = this.rowIdByPageIndexGetter(selectedIndex);
                }
            }

            $dyn.log.list && $dyn.log.list("List: [Data Provider] Recalculated DataSelectedRowId. New value: " + dataSelectedRowId);
            return dataSelectedRowId;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Creates the new pending row that is not committed to server yet
        * @param {Number} _internalRowId - Internal row id of the row that is used as an anchor for new row creation
        * @param {Boolean} isFromKeyDown - Indicates whether the new row is being created from the special 'key down' user action
        * @param {Number} _direction - The direction of the new row creation relative to given internal row id. +1 - below, -1 above
        */
        createRow: function (_internalRowId, isFromKeyDown, _direction) {
            $dyn.log.list && $dyn.log.list.info("List: Entering DataProvider::createRow");

            this._isCreateRowFromKeyDown = isFromKeyDown;
            var that = this;
            var newRowId = this._allocateInternalRowId();
            var pivotInternalRowId = _internalRowId !== undefined ? _internalRowId : this.InternalSelectedRowId;
            var createDirection = _direction || this._rowInsertDirection;

            var newRowOrder = createDirection;
            var pivot;

            function updateOrders(collection, startIndex, direction) {
                for (var i = startIndex; i < collection.length; ++i) {
                    var item = collection[i];
                    if (item) {
                        var id = item.id;
                        if (that.pendingRowToPivot[id] && that.pendingRowToPivot[id].order) {
                            that.pendingRowToPivot[id].order += direction;
                        }
                    }
                }
            }

            if (!this.isRowPending(pivotInternalRowId)) {
                pivot = this.getDataRowIdByInternalRowId(pivotInternalRowId);
                if (!this.pivotToPendingRows[pivot]) {
                    this.pivotToPendingRows[pivot] = {};
                    this.pivotToPendingRows[pivot][CreateRowDirection.RowCreatedBelow] = [];
                    this.pivotToPendingRows[pivot][CreateRowDirection.RowCreatedAbove] = [];
                }

                this.pivotToPendingRows[pivot][createDirection].unshift({
                    id: newRowId,
                    version: 1,
                    marked: false
                });
                // Let's update orders of rows that we shifted
                updateOrders(this.pivotToPendingRows[pivot][createDirection], 1, createDirection);

            } else {
                pivot = this.pendingRowToPivot[pivotInternalRowId].pivot;
                var requestedPivotOrder = this.pendingRowToPivot[pivotInternalRowId].order;
                var requestedPivotDirection = (requestedPivotOrder < 0) ? CreateRowDirection.RowCreatedAbove : CreateRowDirection.RowCreatedBelow;
                var spliceIndex = Math.abs(requestedPivotOrder);
                if (requestedPivotDirection !== createDirection) {
                    spliceIndex = spliceIndex - 1;
                }
                this.pivotToPendingRows[pivot][requestedPivotDirection].splice(spliceIndex, 0, {
                    id: newRowId,
                    version: 1,
                    marked: false,
                });

                // Let's update orders of rows that we shifted
                updateOrders(this.pivotToPendingRows[pivot][requestedPivotDirection], spliceIndex + 1, requestedPivotDirection);

                // Multiplying by order gives new order the right sign
                newRowOrder = requestedPivotDirection * (spliceIndex + 1);
            }

            this.pendingRowToPivot[newRowId] = {
                pivot: pivot,
                order: newRowOrder,
            };

            $dyn.ui.updateBlock(null, function () {
                if ($dyn.isObservable(this.DataSource)) {
                    this.DataSource.valueChanged.raise();
                }
                this.InternalSelectedRowId(newRowId);
            }, this);

            $dyn.log.list && $dyn.log.list.info("List: New row id %s", newRowId);
            $dyn.log.list && $dyn.log.list.info("List: Leaving DataProvider::createRow");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Deletes the row if it is pending row
        * @param {Number} rowId - Internal row id of the row to be deleted
        * @returns {Boolean} The success of the operation
        */
        deleteRow: function (rowId) {
            if (this.isRowPending(rowId)) {
                var pivot = this.pendingRowToPivot[rowId].pivot;
                var pivotInternalRowId = this.getInternalRowId(pivot);
                var order = this.pendingRowToPivot[rowId].order;
                var pendingRowDirection = (order < 0) ? -1 : 1;

                var updateRowSelection = false;
                var nextSelectedRowId = null;
                if ($dyn.peek(this.InternalSelectedRowId) === rowId) {
                    updateRowSelection = true;
                    if(pivot != -1){
                        // Find the row id that is going to be selected after current one is removed
                        var enumerator = this.getEnumerator();
                        enumerator.reset();
                        enumerator.initWithRowId(rowId);
                        if (enumerator.moveNext()) {
                            nextSelectedRowId = enumerator.current().id;
                        }
                    }
                }

                var spliceIndex = Math.abs(order) - 1;
                this.pivotToPendingRows[pivot][pendingRowDirection].splice(Math.abs(order) - 1, 1);
                if (!this.pivotToPendingRows[pivot][1].length && !this.pivotToPendingRows[pivot][-1].length) {
                    delete this.pivotToPendingRows[pivot];
                }
                // Update orders of other pending rows after removal
                if (this.pivotToPendingRows[pivot] && this.pivotToPendingRows[pivot][pendingRowDirection].length) {
                    for (var i = spliceIndex; i < this.pivotToPendingRows[pivot][pendingRowDirection].length; ++i) {
                        var pendingRowId = this.pivotToPendingRows[pivot][pendingRowDirection][i].id;
                        if (this.pendingRowToPivot[pendingRowId] && this.pendingRowToPivot[pendingRowId].order) {
                            this.pendingRowToPivot[pendingRowId].order -= pendingRowDirection;
                        }
                    }
                }
                delete this.pendingRowToPivot[rowId];


                $dyn.ui.updateBlock(null, function () {
                    if ($dyn.isObservable(this.DataSource)) {
                        this.DataSource.valueChanged.raise();
                    }
                    if (updateRowSelection) {
                        if (nextSelectedRowId !== null && nextSelectedRowId !== undefined) {
                            this.InternalSelectedRowId(nextSelectedRowId);
                        } else {
                            this.InternalSelectedRowId(this._getBottomRowId());
                        }
                    }
                }, this);

                return true;
            }
            return false;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Updates internal DataProvider's structures when new row was committed to server
        * @param {Number} internalRowId - Internal row id of the row that was committed to server
        * @param {Integer} selectedIndex - An active page related index of the row that was committed to server
        */
        updatePivotTables: function (internalRowId, selectedIndex) {
            if (internalRowId && selectedIndex >= 0) {
                this.lastDeletedActiveRowId = -1;
                this._firstRowDeleted = false;
                var model = this.Collection()[selectedIndex];

                //GetDataId returns the tracking id
                var newPivot = this._getDataId(model);

                //Update pivotToPendingRows
                //Get existing pivot for pending row
                if (this.pendingRowToPivot[internalRowId] !== undefined) {
                    var pivot = this.pendingRowToPivot[internalRowId].pivot;
                    var order = this.pendingRowToPivot[internalRowId].order;
                    var createDirection = (order < 0) ? CreateRowDirection.RowCreatedAbove : CreateRowDirection.RowCreatedBelow;
                    var pendingRows = this.pivotToPendingRows[pivot][createDirection];

                    this.pivotToPendingRows[pivot][createDirection] = [];

                    if (pendingRows.length > 1) {
                        var sliceIndex = Math.abs(order);
                        var pendingRowsNewPivot = pendingRows.slice(sliceIndex);
                        var pendingRowsOldPivot = pendingRows.slice(0, sliceIndex - 1);
                        if (pendingRowsOldPivot && pendingRowsOldPivot.length > 0) {
                            this.pivotToPendingRows[pivot][createDirection] = pendingRowsOldPivot;
                        }
                        else {
                            this.pivotToPendingRows[pivot] = {};
                            this.pivotToPendingRows[pivot][CreateRowDirection.RowCreatedAbove] = [];
                            this.pivotToPendingRows[pivot][CreateRowDirection.RowCreatedBelow] = [];
                            delete this.pivotToPendingRows[pivot];
                        }
                        if (pendingRowsNewPivot) {
                            this.pivotToPendingRows[newPivot] = {};
                            this.pivotToPendingRows[newPivot][CreateRowDirection.RowCreatedAbove]=[];
                            this.pivotToPendingRows[newPivot][CreateRowDirection.RowCreatedBelow] = [];
                            this.pivotToPendingRows[newPivot][createDirection] = pendingRowsNewPivot;
                        }
                    }

                    //Update pendingRowToPivot
                    if (pendingRowsNewPivot) {
                        for (var i = 0; i < pendingRowsNewPivot.length; i++) {
                            var pendingRowNewPivot = pendingRowsNewPivot[i];
                            this.pendingRowToPivot[pendingRowNewPivot.id].pivot = newPivot;
                            this.pendingRowToPivot[pendingRowNewPivot.id].order = createDirection * (i + 1);
                        }
                    }

                    this._dataRowIdToInternalRowId[newPivot] = internalRowId;
                    this._internalRowIdToDataRowId[internalRowId] = newPivot;

                    var dataSelectedRowId = this.DataSelectedRowId();
                    if (internalRowId !== dataSelectedRowId) {
                        delete this._internalRowIdToDataRowId[dataSelectedRowId];
                    }
                    //This will recompute the value of DataSelectedRowId to be equal to the internalRowId
                    this.DataSelectedRowId.valueChanged.onDependencyChanged();
                    delete this.pendingRowToPivot[internalRowId];
                    this.onRowBecameCommitted.raise(internalRowId);
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Updates internal DataProvider's structures when new row was deleted from server
        * @param {Number} deletedInternalRowId - Internal row id of the row that was last deleted from server
        * @param {Integer} selectedIndex - An active page related index of the row that was committed to server
        * @param {Boolean} onlyCommittedRowDeleted - true if only committed row to server is deleted
        */
        updatePivotTablesAfterDelete: function (deletedInternalRowId, selectedIndex, onlyCommittedRowDeleted) {
            if (deletedInternalRowId) {
                var newPivot;
                if(selectedIndex >= 1){
                    var model = this.Collection()[selectedIndex -1];
                    //_getDataId returns the tracking id
                    newPivot = this._getDataId(model);
                }
                else {
                    if (this.Collection().length >= 1 && !onlyCommittedRowDeleted) {
                        //First row is deleted but it has rows below the pending rows
                        var model = this.Collection()[selectedIndex];
                        newPivot = this._getDataId(model);
                    }
                    else {
                        //Special case when deleting first row
                        //newPivot -1 means first row has error and is deleted and hence pending rows are pivoted against -1.
                        newPivot = -1;
                    }
                    this._firstRowDeleted = true;
                }
                var lastDeletedDataRowId = this._internalRowIdToDataRowId[deletedInternalRowId];
                //pivotToPendingRows is a two dimensional array.
                //first column is the pivot against which it holds the pending rows.
                //second column is the direction, 1(CreateRowDirection.RowCreatedBelow) means rows are below the pivot, -1(CreateRowDirection.RowCreatedAbove) means rows are above the pivot
                //Second column holds array of row objects.
                var pendingRowIdAgainstDeleted = this.pivotToPendingRows[lastDeletedDataRowId];
                var pendingInternalId;
                if (pendingRowIdAgainstDeleted) {
                    if (pendingRowIdAgainstDeleted[CreateRowDirection.RowCreatedBelow] && pendingRowIdAgainstDeleted[CreateRowDirection.RowCreatedBelow].length > 0 && pendingRowIdAgainstDeleted[CreateRowDirection.RowCreatedBelow][0]) {
                        pendingInternalId = pendingRowIdAgainstDeleted[CreateRowDirection.RowCreatedBelow][0].id;
                    }
                    else if (pendingRowIdAgainstDeleted[CreateRowDirection.RowCreatedAbove] && pendingRowIdAgainstDeleted[CreateRowDirection.RowCreatedAbove].length > 0 && pendingRowIdAgainstDeleted[CreateRowDirection.RowCreatedAbove][0]) {
                        pendingInternalId = pendingRowIdAgainstDeleted[CreateRowDirection.RowCreatedAbove][0].id;
                    }
                }
                //Update pivotToPendingRows
                //Get existing pivot for pending row
                if (pendingInternalId && this.pendingRowToPivot[pendingInternalId] !== undefined) {
                    var pivot = this.pendingRowToPivot[pendingInternalId].pivot;
                    var order = this.pendingRowToPivot[pendingInternalId].order;
                    //Order is where the row is situated. 1(CreateRowDirection.RowCreatedBelow) means above the pivot and -1(CreateRowDirection.RowCreatedAbove) means below the pivot.
                    var createDirection = (order < 0) ? CreateRowDirection.RowCreatedAbove : CreateRowDirection.RowCreatedBelow;
                    var pendingRows = this.pivotToPendingRows[pivot][createDirection];

                    delete this.pivotToPendingRows[pivot];

                    if (pendingRows) {
                        //pivotToPendingRows is a two dimensional array.
                        //first column is the pivot against which it holds the pending rows.
                        //second column is the direction, 1(CreateRowDirection.RowCreatedBelow) means rows are below the pivot, -1(CreateRowDirection.RowCreatedAbove) means rows are above the pivot
                        //Second column holds array of row objects.
                        this.pivotToPendingRows[newPivot] = {};
                        this.pivotToPendingRows[newPivot][CreateRowDirection.RowCreatedAbove] = [];
                        this.pivotToPendingRows[newPivot][CreateRowDirection.RowCreatedBelow] = [];
                        this.pivotToPendingRows[newPivot][createDirection] = pendingRows;
                        var internalId;
                        for (var i = 0; i < pendingRows.length; i++) {
                            var pendingRowNewPivot = pendingRows[i];
                            this.pendingRowToPivot[pendingRowNewPivot.id].pivot = newPivot;
                            this.pendingRowToPivot[pendingRowNewPivot.id].order = createDirection * (i + 1);
                            if (i == 0) {
                                internalId = pendingRowNewPivot.id;
                            }
                        }
                        //newPivot -1 means first row has error and is deleted and hence pending rows are pivoted against -1.
                        if (newPivot == -1) {
                            this._dataRowIdToInternalRowId[newPivot] = internalId;
                            this._internalRowIdToDataRowId[internalId] = newPivot;
                        }
                    }

                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Updates data id usage after data id of the same record was changed
        * @param {Number} oldDataId - Old data id
        * @param {Integer} newDataId - New data id
        */
        updateDataId: function (oldDataId, newDataId) {
            var that = this;
            var internalRowId = this._dataRowIdToInternalRowId[oldDataId];
            if (internalRowId) {
                delete this._dataRowIdToInternalRowId[oldDataId];
                this._dataRowIdToInternalRowId[newDataId] = internalRowId;
                this._internalRowIdToDataRowId[internalRowId] = newDataId;
            }
            var pendingRows = this.pivotToPendingRows[oldDataId];
            if (pendingRows) {
                var above = pendingRows[-1];
                var below = pendingRows[1];
                var updatePendingRowToPivot = function (rows) {
                    for (var i = 0; i < rows.length; ++i) {
                        var pendingRow = rows[i];
                        if (pendingRow) {
                            that.pendingRowToPivot[pendingRow.id].pivot = newDataId;
                        }
                    }
                };
                if (above) {
                    updatePendingRowToPivot(above);
                }
                if (below) {
                    updatePendingRowToPivot(below);
                }
                this.pivotToPendingRows[newDataId] = pendingRows;
                delete this.pivotToPendingRows[oldDataId];
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Changes the row to the given row id
        * @param {Number} internalRowId - The row id
        */
        changeRow: function (internalRowId) {
            $dyn.log.list && $dyn.log.list.info("List: Entering DataProvider::changeRow for internalRowId %s", internalRowId);
            // Force internal selected row change
            this.InternalSelectedRowId(internalRowId, true);
            $dyn.log.list && $dyn.log.list.info("List: Leaving DataProvider::changeRow");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Synchronizes internal selected row id to data selected row id
        */
        syncInternalSelectedRowToDataSelectedRow: function () {
            this.InternalSelectedRowId($dyn.peek(this.DataSelectedRowId));
            $dyn.log.list && $dyn.log.list("List: [Data Provider] Synced internal selected row id to data selected row id. New value: " + $dyn.peek(this.InternalSelectedRowId));
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets data enumerator
        * @returns {Object} Enumerator
        */
        getEnumerator: function () {
            return this._enumerator;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets or creates the internal row id by given data row id.
        * @param {Number} dataRowId - Data row id
        * @returns {Number} Internal row id
        */
        getOrCreateInternalRowId: function (dataRowId) {
            var internalRowId = this._dataRowIdToInternalRowId[dataRowId];
            if (!internalRowId) {
                internalRowId = this._allocateInternalRowId();
                this._dataRowIdToInternalRowId[dataRowId] = internalRowId;
                this._internalRowIdToDataRowId[internalRowId] = dataRowId;
            }

            return internalRowId;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Gets an internal temporary flag that indicates whether the just was created by 'key down' gesture
        * @returns {Boolean} The value of the temporary flag
        */
        getIsCreateRowFromKeyDown: function () {
            return this._isCreateRowFromKeyDown;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Sets an internal temporary flag that indicates whether the just was created by 'key down' gesture
        * @param {Boolean} isCreateRowOnKeyDown - The value of the temporary flag
        */
        setIsCreateRowFromKeyDown: function (isCreateRowOnKeyDown) {
            this._isCreateRowFromKeyDown = isCreateRowOnKeyDown;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets an internal row id by given data row id.
        * @param {Number} dataRowId - Data row id
        * @returns {Number} Internal row id
        */
        getInternalRowId: function (dataRowId) {
            return this._dataRowIdToInternalRowId[dataRowId];
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets page index by given internal row id. Note the method has linear complexity
        * @param {Number} internalRowId - Internal row id
        * @returns {Number} Page index
        */
        getPageIndexByInternalRowId: function (internalRowId) {
            return this.getPageIndexByDataRowId(this.getDataRowIdByInternalRowId(internalRowId));
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets page index by given internal row id. Note the method has linear complexity
        * @param {Number} internalRowId - Internal row id
        * @returns {Number} Page index
        */
        getDataRowIdByInternalRowId: function (internalRowId) {
            return this._internalRowIdToDataRowId[internalRowId];
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets the data row id that given internal row id anchored to (in case given internal row id represents the pending row)
        * @param {Number} internalRowId - Internal row id
        * @returns {Number} Data row id
        */
        getRelativeDataRowIdByInternalRowId: function (internalRowId) {
            var relativeRowId = undefined;
            var pivotInfo = this.pendingRowToPivot[internalRowId];
            if (pivotInfo) {
                relativeRowId = pivotInfo.pivot;
            }
            return relativeRowId;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets page index by given data row id. Note the method has linear complexity
        * @param {Number} dataRowId - Data row id
        * @returns {Number} Page index
        */
        getPageIndexByDataRowId: function (dataRowId) {
            var collection = $dyn.value(this.Collection);
            if (collection) {
                for (var pageIndex = 0; pageIndex < collection.length; ++pageIndex) {
                    var dataItem = collection[pageIndex];
                    if (dataItem) {
                        var dataId = this._getDataId(dataItem);
                        if (dataId === dataRowId) {
                            return pageIndex;
                        }
                    }
                }
            }

            return undefined;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets data row id by given page index
        * @param {Number} pageIndex - Page index
        * @returns {Number} Data row id
        */
        getDataRowIdByPageIndex: function (pageIndex) {
            var collection = $dyn.value(this.Collection);
            if (collection) {
                var dataItem = collection[pageIndex];
                if (dataItem) {
                    return this._getDataId(dataItem);
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets internal row id by given page index
        * @param {Number} pageIndex - Page index
        * @returns {Number} Internal row id
        */
        getInternalRowIdByPageIndex: function (pageIndex) {
            var dataRowId = this.getDataRowIdByPageIndex(pageIndex);
            return this._dataRowIdToInternalRowId[dataRowId];
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Gets row data by given internal row id
        * @param {Number} internalRowId - Internal row id
        * @returns {Object} Row data
        */
        _getDataByInternalRowId: function (internalRowId) {
            // Could improve: This function should use a map instead of getting an index that is costly operation
            var collection = $dyn.value(this.Collection);
            var pageIndex = this.getPageIndexByInternalRowId(internalRowId);
            if (pageIndex >= 0 && pageIndex < collection.length) {
                return collection[pageIndex];
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets data provider item (that is an extended data item) by given internal row id
        * @param {Number} internalRowId - Internal row id
        * @returns {$dyn.controls.List.DataProvider.DataProviderItem} Data provider item
        */
        getDataProviderItemByInternalRowId: function (internalRowId) {
            // Could improve: This function should use a map instead of getting an index that is costly operation
            var pageIndex = this.getPageIndexByInternalRowId(internalRowId);
            return this._getDataProviderItemByIndex(pageIndex);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets first data provider item (that is an extended data item) by given internal row id
        * @returns {$dyn.controls.List.DataProvider.DataProviderItem} Data provider item
        */
        getFirstItem: function () {
            return this._getDataProviderItemByIndex(0);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Gets last data provider item (that is an extended data item) by given internal row id
        * @returns {$dyn.controls.List.DataProvider.DataProviderItem} Data provider item
        */
        getLastItem: function () {
            var collection = $dyn.value(this.Collection);
            if (collection.length) {
                return this._getDataProviderItemByIndex(collection.length - 1);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Indicates whether the row is the backend last row
        * @param {number} internalRowId
        * @returns {Boolean} True if the row is the backend last row
        */
        isBackendLastRow: function (internalRowId) {
            var collection = $dyn.peek(this.Collection);
            if (!this._isActivePageLast() || !collection.length) {
                return false;
            }

            var existingRowId = internalRowId;
            if (this.isRowPending(internalRowId)) {
                existingRowId = this._dataRowIdToInternalRowId[this.pendingRowToPivot[internalRowId].pivot];
            }
            var collection = $dyn.peek(this.Collection);
            var lastCollectionItem = collection[collection.length - 1];
            var lastCollectionItemId = this._dataRowIdToInternalRowId[this._getDataId(lastCollectionItem)];
            return lastCollectionItemId === existingRowId;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Indicates whether the given page index is within active page
        * @param {number} pageIndex
        * @returns {Boolean} True if the given page index is within active page
        */
        isIndexWithinPage: function (pageIndex) {
            return pageIndex >= 0 && pageIndex < this.getPageLength();
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Indicates whether the currently active page is last on the back end
        * @returns {Boolean} True if the currently active page is last on the back end
        */
        _isActivePageLast: function () {
            var collection = $dyn.peek(this.Collection);
            var cacheSize = $dyn.peek(this.CacheSize);
            var pageStart = $dyn.peek(this.FetchedDataStartPosition);

            return !$dyn.value(this.DataBelow) && collection.length + pageStart >= cacheSize;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Gets data provider item (that is an extended data row item) by page index
        * @param {Number} index - Page index
        * @returns {$dyn.controls.List.DataProvider.DataProviderItem} Data provider item
        */
        _getDataProviderItemByIndex: function (index) {
            var collection = $dyn.value(this.Collection);
            var dataItem = collection && collection[index];
            if (dataItem) {
                // Could improve: Store map of such objects as we may be invoking it too often
                var dataItemId = this._getDataId(dataItem);
                var internalRowId = this.getOrCreateInternalRowId(dataItemId);
                var version = this._getDataVersion(dataItem);
                var dataSelectedRowId = $dyn.peek(this.DataSelectedRowId);
                return $.extend({}, dataItem, {
                    id: internalRowId,
                    marked: $dyn.peek(this.MarkedIndexes)[index],
                    version: version,
                    selected: dataSelectedRowId === internalRowId,
                    type: this.committedRowType,
                });
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Gets pending row item (that has same contract as data provider item but represents pending row) by page index
        * @param {Number} pivotId - Data row id of the pending row pivot
        * @param {Number} index - Page index
        * @returns {$dyn.controls.List.DataProvider.DataProviderItem} Pending row item
        */
        _getPendingRowItemByIndex: function (pivotId, index) {
            var pendingRowsByPivot = this.pivotToPendingRows[pivotId];
            if (pendingRowsByPivot) {
                var pendingRowCollection;
                if (index < 0) {
                    pendingRowCollection = pendingRowsByPivot[-1];
                } else {
                    pendingRowCollection = pendingRowsByPivot[1];
                }
                return pendingRowCollection[Math.abs(index) - 1];
            }

        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Gets pending row item (that has same contract as data provider item but represents pending row) by page index
        * @param {Number} pivotId - Data row id of the pending row pivot
        * @param {Number} index - Page index
        * @returns {pendingRowCollection} pending row collection
        */
        _getPendingRowItemCollectionByIndex: function (pivotId, index) {
            var pendingRowsByPivot = this.pivotToPendingRows[pivotId];
            if (pendingRowsByPivot) {
                var pendingRowCollection;
                if (index < 0) {
                    pendingRowCollection = pendingRowsByPivot[-1];
                } else {
                    pendingRowCollection = pendingRowsByPivot[1];
                }
                return pendingRowCollection;
            }

        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Gets the pending row index of the top pending row by the given pivot id
        * @param {Number} pivotId - Data row id of the pending row pivot
        * @returns {Number} Pendig row index
        */
        _getTopPendingRowIndexByPivot: function (pivotId) {
            var pivotToPendingRows = this.pivotToPendingRows[pivotId];
            if (pivotToPendingRows) {
                var pendingRowsAbove = pivotToPendingRows[-1];
                return (-pendingRowsAbove.length);
            }
            return 0;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Gets the pending row index of the bottom pending row by the given pivot id
        * @param {Number} pivotId - Data row id of the pending row pivot
        * @returns {Number} Pendig row index
        */
        _getBottomPendingRowIndexByPivot: function (pivotId) {
            var pivotToPendingRows = this.pivotToPendingRows[pivotId];
            if (pivotToPendingRows) {
                var pendingRowsBelow = pivotToPendingRows[1];
                return pendingRowsBelow.length;
            }
            return 0;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @private
        * @summary Returns the internal row id of the bottom-most row (can be pending row as well)
        * @returns {Number} Row id
        */
        _getBottomRowId: function () {
            var collection = $dyn.value(this.Collection);
            if (!collection || !collection.length) {
                return null;
            }

            var dataRowId = this.getDataRowIdByPageIndex(collection.length - 1);
            var internalRowId = this.getInternalRowId(dataRowId);
            var bottomPendingRowIndex = this._getBottomPendingRowIndexByPivot(dataRowId);
            if (bottomPendingRowIndex) {
                return this.pivotToPendingRows[dataRowId][1][bottomPendingRowIndex - 1].id;
            }
            return internalRowId;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Get the length of the data source
        * @returns {Number} Page length
        */
        getPageLength: function () {
            return $dyn.peek(this.Collection).length;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider#
        * @internal
        * @summary Disposes the data provider
        */
        dispose: function () {
            for (var i = 0; i < this._disposables.length; ++i) {
                var disposable = this._disposables[i];
                if (disposable && disposable.dispose && typeof(disposable.dispose) === "function") {
                    disposable.dispose();
                }
            }
        },
    };

    /**
     * @class Enumerator
     * @classdesc The data enumerator of the DataProvider
     * @memberof $dyn.controls.List.DataProvider
     * @internal
     * @param {$dyn.controls.List.DataProvider} dataProvider
     */
    $dyn.controls.List.DataProvider.Enumerator = function (dataProvider) {
        this._dataProvider = dataProvider;
        this.reset();
    };

    $dyn.controls.List.DataProvider.Enumerator.prototype = {
        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @private
        * @summary The data provider
        * @type {$dyn.controls.List.DataProvider}
        */
        _dataProvider: null,

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @private
        * @summary Current collection index (0-based)
        * @type {Number}
        */
        _collectionIndex: null,

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @private
        * @summary Current pending row index (1-based)
        * @type {Number}
        */
        _pendingRowIndex: null,

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @private
        * @summary Indicates whether an enumerator is currently pointing before collection (unintialized)
        * @type {Boolean}
        */
        _beforeCollection: true,

        /**
        * @member
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @private
        * @summary Indicates whether an enumerator is currently pointing after collection (reached the end)
        * @type {Boolean}
        */
        _afterCollection: false,

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @internal
        * @summary Resets the enumerator to its default state. After rest an enumerator points before collection.
        */
        reset: function () {
            var collection = $dyn.value(this._dataProvider.Collection);
            if (collection && collection.length > 0) {
                this._collectionIndex = 0;
                var pivotId = this._dataProvider._getDataId(collection[this._collectionIndex]);
                this._pendingRowIndex = this._dataProvider._getTopPendingRowIndexByPivot(pivotId) || 0;
                this._beforeCollection = true;
                this._afterCollection = false;
            } else {
                this._collectionIndex = null;
                this._pendingRowIndex = null;
                this._beforeCollection = false;
                this._afterCollection = true;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @internal
        * @summary Initializes enumerator with the given row id
        * @param {Number} rowId - Row id
        */
        initWithRowId: function (rowId) {
            if (this._dataProvider.isRowPending(rowId)) {
                if (!this._dataProvider.pendingRowToPivot[rowId]) {
                    return;
                }
                var pivotDataId = this._dataProvider.pendingRowToPivot[rowId].pivot;
                var pendingRowIndex = this._dataProvider.pendingRowToPivot[rowId].order;
                var pivotCollectionIndex = this._dataProvider.getPageIndexByDataRowId(pivotDataId);
                this._collectionIndex = pivotCollectionIndex;
                this._pendingRowIndex = pendingRowIndex;
            } else {
                this._collectionIndex = this._dataProvider.getPageIndexByInternalRowId(rowId);
                this._pendingRowIndex = 0;
            }
            this._beforeCollection = false;
            this._afterCollection = false;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @internal
        * @summary Gets current item of the enumerator
        * @param {firstRowDeleted} firstRowDeleted - true when first row of the grid has pending rows pivoted against and gets deleted
        * @param {position} position of the item
        * @returns {$dyn.controls.List.DataProvider.DataProviderItem} Current enumerator value
        */
        current: function (firstRowDeleted, position) {
            var dataProviderItem;
            if (!this._beforeCollection && !this._afterCollection) {
                var collection = $dyn.value(this._dataProvider.Collection);

                if (this._collectionIndex !== null && this._collectionIndex !== undefined) {
                    var item = collection[this._collectionIndex];
                    var dataRowId = this._dataProvider._getDataId(item);
                    if (!dataRowId && firstRowDeleted) {
                        dataRowId = -1;
                        var pendingCollection = this._dataProvider._getPendingRowItemCollectionByIndex(dataRowId, 1);
                        if (pendingCollection && pendingCollection.length > position) {
                            dataProviderItem = pendingCollection[position];
                        }
                    }
                    else if (this._pendingRowIndex === 0) {
                        // Current row is existing row in data source
                        dataProviderItem = this._dataProvider._getDataProviderItemByIndex(this._collectionIndex);
                    } else {
                        // Current row is pending row
                        dataProviderItem = this._dataProvider._getPendingRowItemByIndex(dataRowId, this._pendingRowIndex);
                    }

                }
            }
            return dataProviderItem;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.DataProvider.Enumerator#
        * @internal
        * @summary Moves an enumerator to the next item
        * @returns {Boolean} The success of the operation
        */
        moveNext: function () {
            var that = this;
            // If enumerator passed the 'end' of the collection, return false
            if (this._afterCollection) {
                return false;
            }

            // If enumerator is still before the 'start' of the collection, return true
            if (this._beforeCollection) {
                this._beforeCollection = false;
                return true;
            }

            function incrementCollectionIndex() {
                that._collectionIndex++;
                if (that._collectionIndex >= collection.length) {
                    that._afterCollection = true;
                    return false;
                } else {
                    var item = collection[that._collectionIndex];
                    var dataRowId = that._dataProvider._getDataId(item);
                    that._pendingRowIndex = that._dataProvider._getTopPendingRowIndexByPivot(dataRowId) || 0;
                    return true;
                }
            }

            var collection = $dyn.value(this._dataProvider.Collection);
            var item = collection[this._collectionIndex];
            var dataRowId = this._dataProvider._getDataId(item);
            var pivotToPendingRows = this._dataProvider.pivotToPendingRows[dataRowId];
            var pendingRowsAbove;
            var pendingRowsBelow;
            if (pivotToPendingRows) {
                pendingRowsAbove = pivotToPendingRows[-1];
                pendingRowsBelow = pivotToPendingRows[1];
            }
            if (this._pendingRowIndex === 0) {
                // Current row is existing row in data source
                // If this row have appropriate pending rows below, let's move to first pending row below
                if (pendingRowsBelow && pendingRowsBelow.length) {
                    this._pendingRowIndex = 1;
                    return true;
                } else {
                    // Otherwisw let's proceed to next existing row
                    return incrementCollectionIndex();
                }
            } else {
                // Current row is pending row
                // In case current row was some pending row above, let's just move one row down
                if (this._pendingRowIndex < 0) {
                    this._pendingRowIndex++;
                    return true;
                } else {
                    // Otherwise current row is the pending row below
                    this._pendingRowIndex++;
                    if (!pendingRowsBelow || this._pendingRowIndex > pendingRowsBelow.length) {
                        return incrementCollectionIndex();
                    }
                    return true;
                }
            }
        },
    };

})();